import React, { useState, useEffect } from "react";
import "./AllTable.scss";
import TableButton from "../components/TableButton";
import TablePopup from "../components/TablePopup";
import baseUrl from "../../../../api/baseUrl";
import InputText from "../../../../components/inputs/InputText";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import useSubmitting from "../../../../hooks/useSubmitting";

const statuses = [
  { label: "Available", color: "#0A2398" },
  { label: "Not Ordered", color: "#D7D7D7" },
  { label: "Ordered", color: "#F2A91C" },
  { label: "Ongoing", color: "#4763e4" },
  { label: "Payment Initiated", color: "#067904" },
  { label: "Uncleaned", color: "#DC1818" },
  { label: "Reserved", color: "#3FC091" },
];

function AllTable() {
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [popupData, setPopupData] = useState<{
    tableNumber: string;
    peopleCount: number;
    tableId?: number;
  } | null>(null);

  const [showAddTable, setShowAddTable] = useState(false);
  const [addTableLoading, setAddTableLoading] = useState(false);
  const [addTableForm, setAddTableForm] = useState({
    tableNumber: "",
    person: "",
    discription: "",
    status: "Available",
  });
  const [tableData, setTableData] = useState<any[]>([]);
  const [statusLoading, setStatusLoading] = useState<number | null>(null);

  // Get all table orders from redux
  const allTableOrders = useSelector(
    (state: RootState) => state.tableOrder.allTableOrders,
  );

  useEffect(() => {
    fetchTableData();
    // eslint-disable-next-line
  }, []);

  const fetchTableData = () => {
    baseUrl
      .get("/branch/table/get-tables")
      .then((res) => {
        setTableData(
          (res.data.data || []).map((t: any) => ({
            tableId: t.id,
            tableNumber: t.tableNumber?.toString(),
            tableTime: "",
            peopleCount: t.person,
            status: t.status,
            showPeople: true,
            showArrow: true,
            color:
              statuses.find((s) => s.label === t.status)?.color || "#D7D7D7",
          })),
        );
      })
      .catch(() => setTableData([]));
  };

  // Merge redux status into tableData for display
  const mergedTableData = tableData.map((table) => {
    const reduxTable = allTableOrders.find(
      (t: any) =>
        (t.tableId && t.tableId === table.tableId) ||
        (!t.tableId && t.tableNumber === table.tableNumber),
    );
    if (reduxTable && reduxTable.status) {
      return {
        ...table,
        status: reduxTable.status,
        color:
          statuses.find((s) => s.label === reduxTable.status)?.color ||
          table.color,
      };
    }
    return table;
  });

  // Update table status via API and update UI only after success
  const handleStatusChange = async (tableId: number, newStatus: string) => {
    setStatusLoading(tableId);
    try {
      await baseUrl.patch(`/branch/table/update-table-status/${tableId}`, {
        status: newStatus,
      });
      // Only update UI after API success
      setTableData((prev) =>
        prev.map((t) =>
          t.tableId === tableId
            ? {
                ...t,
                status: newStatus,
                color:
                  statuses.find((s) => s.label === newStatus)?.color || t.color,
              }
            : t,
        ),
      );
    } catch {
      // Optionally show error
    }
    setStatusLoading(null);
  };

  const toggleStatus = (status: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
  };

  const filteredTables =
    selectedStatuses.length > 0
      ? mergedTableData.filter((table) =>
          selectedStatuses.includes(table.status),
        )
      : mergedTableData;

  const handleTableClick = (
    tableNumber: string,
    peopleCount: number,
    tableId?: number,
  ) => {
    setPopupData({ tableNumber, peopleCount, tableId });
  };

  const handleAddTableChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAddTableForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddTableSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddTableLoading(true);
    try {
      await baseUrl.post("/branch/table/create-table", {
        tableNumber: parseInt(addTableForm.tableNumber, 10),
        person: parseInt(addTableForm.person, 10),
        discription: addTableForm.discription,
        status: "Available",
      });
      alert("Table added successfully!");
      setShowAddTable(false);
      setAddTableForm({
        tableNumber: "",
        person: "",
        discription: "",
        status: "Available",
      });
      // Refresh table list after adding
      baseUrl
        .get("/branch/table/get-tables")
        .then((res) => {
          setTableData(
            (res.data.data || []).map((t: any) => ({
              tableId: t.id,
              tableNumber: t.tableNumber?.toString(),
              tableTime: "",
              peopleCount: t.person,
              status: t.status,
              showPeople: true,
              showArrow: true,
              color:
                statuses.find((s) => s.label === t.status)?.color || "#D7D7D7",
            })),
          );
        })
        .catch(() => setTableData([]));
    } catch (err: any) {
      alert(err?.response?.data?.message || "Failed to add table");
    }
    setAddTableLoading(false);
  };
  const [submitting, wrap] = useSubmitting();

  return (
    <div className="alltable">
      <div className="checkbox_list">
        {statuses.map((item, index) => (
          <div className="checkbox_item" key={index}>
            <input
              type="checkbox"
              id={`status-${index}`}
              checked={selectedStatuses.includes(item.label)}
              onChange={() => toggleStatus(item.label)}
            />
            <label htmlFor={`status-${index}`}>{item.label}</label>
          </div>
        ))}
      </div>

      <div className="table">
        {filteredTables.map((table, index) => (
          <div key={index} style={{ position: "relative" }}>
            <TableButton
              tableNumber={table.tableNumber}
              tableTime={table.tableTime}
              peopleCount={table.peopleCount}
              showPeople={table.showPeople}
              showArrow={table.showArrow}
              backgroundColor={table.color}
              color={table.color}
              onClick={() =>
                handleTableClick(
                  table.tableNumber,
                  table.peopleCount,
                  table.tableId,
                )
              }
            />
            {/* Status select for each table */}
            <div style={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}>
              <select
                value={table.status}
                onChange={(e) =>
                  handleStatusChange(table.tableId, e.target.value)
                }
                disabled={statusLoading === table.tableId}
                style={{
                  fontSize: 12,
                  borderRadius: 4,
                  padding: "2px 6px",
                  border: "1px solid #ccc",
                  background: "#fff",
                  color: "#222",
                }}
              >
                {statuses.map((s) => (
                  <option key={s.label} value={s.label}>
                    {s.label}
                  </option>
                ))}
              </select>
              {statusLoading === table.tableId && (
                <span style={{ marginLeft: 6, color: "#888" }}>...</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="color_list">
        {statuses.map((item, index) => (
          <div className="color_status" key={index}>
            <span
              style={{ backgroundColor: item.color }}
              className="color_dot"
            ></span>
            <p>{item.label}</p>
          </div>
        ))}
      </div>

      {popupData && (
        <TablePopup
          tableNumber={popupData.tableNumber}
          peopleCount={popupData.peopleCount}
          tableId={popupData.tableId}
          onClose={() => setPopupData(null)}
        />
      )}

      {/* Floating Add Table Button */}
      <button
        className="add-table-fab"
        onClick={() => setShowAddTable(true)}
        title="Add Table"
      >
        +
      </button>

      {/* Add Table Modal */}
      {showAddTable && (
        <div className="add-table-modal">
          <form
            className="add-table-form"
            onSubmit={wrap(handleAddTableSubmit)}
          >
            <h3>Add Table</h3>
            <InputText
              name="tableNumber"
              type="number"
              label="Table Number"
              value={addTableForm.tableNumber}
              onChange={handleAddTableChange}
              required
            />
            <InputText
              name="person"
              type="number"
              label="Number of Persons"
              value={addTableForm.person}
              onChange={handleAddTableChange}
              required
            />
            <InputText
              name="discription"
              type="text"
              label="Description"
              value={addTableForm.discription}
              onChange={handleAddTableChange}
              placeholder="(Optional)"
            />
            <InputText
              name="status"
              type="text"
              label="Status"
              value="Available"
              onChange={() => {}}
              isDisable
            />
            <div className="add-table-actions">
              <button type="submit" disabled={addTableLoading || submitting}>
                {addTableLoading || submitting ? "Adding..." : "Add"}
              </button>
              <button type="button" onClick={() => setShowAddTable(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Add styles for modal and fab */}
      <style>{`
        .add-table-fab {
          position: fixed;
          bottom: 32px;
          right: 32px;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: #4763e4;
          color: #fff;
          font-size: 2.5rem;
          border: none;
          box-shadow: 0 2px 8px rgba(0,0,0,0.18);
          cursor: pointer;
          z-index: 1200;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .add-table-modal {
          position: fixed;
          top: 0; left: 0; width: 100vw; height: 100vh;
          background: rgba(0,0,0,0.3);
          display: flex; align-items: center; justify-content: center;
          z-index: 1300;
        }
        .add-table-form {
          background: #fff;
          padding: 32px 24px 24px 24px;
          border-radius: 10px;
          min-width: 320px;
          max-width: 95vw;
          display: flex;
          flex-direction: column;
          gap: 18px;
        }
        .add-table-form label {
          display: flex;
          flex-direction: column;
          font-weight: 500;
          color: #333;
          gap: 4px;
        }
        .add-table-form input, .add-table-form select {
          padding: 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
          outline: none;
        }
        .add-table-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }
        .add-table-actions button[type="submit"] {
          background: #4763e4;
          color: #fff;
          border: none;
          border-radius: 5px;
          padding: 8px 18px;
          font-weight: 600;
          cursor: pointer;
        }
        .add-table-actions button[type="button"] {
          background: #ccc;
          color: #333;
          border: none;
          border-radius: 5px;
          padding: 8px 18px;
          font-weight: 500;
          cursor: pointer;
        }
        @media (max-width: 900px) {
          .add-table-fab {
            bottom: 16px;
            right: 16px;
            width: 48px;
            height: 48px;
            font-size: 2rem;
          }
          .add-table-form {
            padding: 18px 8px 16px 8px;
            min-width: 90vw;
          }
        }
      `}</style>
    </div>
  );
}

export default AllTable;
